/**
 * Sterrenweergave (0–5). Stond eerst als lokale `Stars` in ReviewsPage; sinds de productpagina
 * óók beoordelingen toont (met een aggregateRating in de structuurdata die daarbij moet kloppen)
 * hoort er één component te zijn — anders lopen de twee weergaves uit elkaar.
 *
 * Groen in plaats van goud, en met het aantal beoordelingen er direct achter: dat is het patroon
 * dat webshopbezoekers kennen (Coolblue, Trustpilot, Kiyoh) en het leest sneller dan een cijfer.
 * Groen is hier `success` — bewust NIET de `buy`-groene, die is voorbehouden aan de koopknop.
 *
 * De vulling is fijnmazig: een 4,3 toont vier hele sterren plus 30% van de vijfde. Dat is geen
 * detail maar de kern van de weergave — met afronden op hele sterren zag 4,5 er hetzelfde uit als
 * 4,9 en zou een dalend cijfer onzichtbaar blijven. De halve ster ontstaat door een tweede,
 * gevulde ster over de lege heen te leggen en die op breedte af te knippen.
 */

const PAD =
  "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z";

function Ster({ vulling, size }: { vulling: number; size: number }) {
  return (
    <span className="relative inline-block align-middle" style={{ width: size, height: size }}>
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="absolute inset-0 text-neutral-200" fill="currentColor">
        <path d={PAD} />
      </svg>
      {vulling > 0 && (
        // overflow-hidden + breedte = de knip. De ster erbinnen houdt zijn volle maat, dus je ziet
        // écht een deel van een ster en geen samengeknepen sterretje.
        <span className="absolute inset-0 overflow-hidden" style={{ width: `${vulling * 100}%` }}>
          <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" className="text-success-500" fill="currentColor">
            <path d={PAD} />
          </svg>
        </span>
      )}
    </span>
  );
}

export function ReviewStars({
  value,
  size = 20,
  count,
  countLabel = "reviews",
  className = "",
}: {
  value: number;
  /** Hoogte/breedte van één ster in pixels. */
  size?: number;
  /** Aantal beoordelingen; staat achter de sterren. Weglaten = alleen sterren. */
  count?: number;
  /** Woord achter het aantal ("reviews", "beoordelingen") — meertalig aan te leveren. */
  countLabel?: string;
  className?: string;
}) {
  const cijfer = Math.max(0, Math.min(5, value));
  return (
    <span
      className={`inline-flex items-center gap-2 ${className}`}
      aria-label={
        count == null
          ? `${cijfer.toFixed(1).replace(".", ",")} van 5`
          : `${cijfer.toFixed(1).replace(".", ",")} van 5, ${count} ${countLabel}`
      }
    >
      <span className="inline-flex items-center gap-0.5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Ster key={i} vulling={Math.max(0, Math.min(1, cijfer - i))} size={size} />
        ))}
      </span>
      {count != null && (
        <span aria-hidden="true" className="text-sm font-medium text-primary-700">
          {count} {countLabel}
        </span>
      )}
    </span>
  );
}
